// Loads, injects, translates and initializes shared components (Header, Footer)
// declared in the page registry. Knows no specific page (§3 boundary).

import { injectHtml } from '/shared/utils/core/dom.js';
import { loadStyles } from '/shared/utils/performance/index.js';

export default class ComponentManager {
  /**
   * @param {{ i18n: import('./I18nCore.js').default, registry: object }} options
   */
  constructor({ i18n, registry }) {
    this.i18n = i18n;
    this.registry = registry;
  }

  /**
   * Load a registered component by name into its target container.
   * @param {string} name e.g. "header" | "footer"
   * @returns {Promise<void>}
   */
  async load(name) {
    const config = this.registry.components?.[name];
    if (!config) {
      console.error(`[components] unknown component "${name}"`);
      return;
    }

    const container = document.querySelector(config.target);
    if (!container) return; // Target absent on this page: nothing to do.

    try {
      // Its own stylesheet, when it declares one: started BEFORE the HTML lands, so the
      // component is never painted unstyled, and not awaited — loadStyles resolves on an
      // error too, and a missing sheet must not keep the markup off the page.
      if (config.css) loadStyles([config.css]);
      const res = await fetch(config.html);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      injectHtml(container, await res.text());
      await this.i18n.apply(container);

      const module = await import(config.module);
      module[config.init]?.(container);
    } catch (err) {
      console.error(`[components] failed to load "${name}":`, err.message);
    }
  }
}
